/**
 * Task-node state vocabulary — the single source of truth for how a
 * `TaskNode.state` maps to its user-facing label, colour, and glyph.
 *
 * Issue #759 (Phase 2, the two-way editor). Pure, dependency-free: both
 * the canvas card and the outline rail read these so the four states look
 * identical everywhere (a node card, a chip, a status dot).
 *
 * Design refs:
 *   - §A6 (state + lifecycle): the four-state set is *factual, not
 *     judgmental*. "Blocked" is about the WORK, not the person.
 *   - §A9 (brand firewall): "Blocked" renders in **calm amber**, never
 *     red/alarm. Copy is factual ("Blocked"), never evaluative.
 *   - Wireframe colours extracted verbatim from `B2-1-map-home.svg` so the
 *     built surface matches the gallery pixel-for-pixel:
 *       done        → #059669 on #ECFDF5
 *       in_progress → #4F46E5 on #EEF2FF
 *       blocked     → calm amber (#B45309 on #FEF3C7)
 *       planned     → #9CA3AF on #F3F4F6
 *
 * The amber for "blocked" is intentionally chosen here (the gallery's
 * worked example happens not to show a blocked node on B2.1); it is the
 * calm-amber B8.2 specifies, deliberately distinct from any danger/alarm
 * red the rest of the app uses.
 */

/** The wire enum, mirrored from `schema.gen.ts` `TaskNodeRead.state`. */
export type TaskState = "planned" | "in_progress" | "blocked" | "done";

export interface TaskStateStyle {
  /** The wire value. */
  readonly state: TaskState;
  /** User-facing label (factual, never evaluative). */
  readonly label: string;
  /** Glyph shown before the label in chips / dots. */
  readonly glyph: string;
  /** Foreground (text + dot) colour, hex. */
  readonly fg: string;
  /** Chip background colour, hex. */
  readonly chipBg: string;
  /**
   * Standalone glyph/dot colour, hex. Optional and additive: when omitted
   * the dot/glyph uses `fg` (the legacy behaviour — `fg` doubles as the dot
   * colour). The editorial theme sets this to the saturated state colour
   * while `fg` is a darker chip-TEXT colour that stays readable on `chipBg`.
   * Consumers that draw a bare dot should read `dot ?? fg`.
   */
  readonly dot?: string;
}

/**
 * The four states, in lifecycle order. Iterating this array is how the
 * focused-card "state swatch row" (B2.2) renders its four dots, so the
 * order here is the order the swatches appear.
 */
export const TASK_STATES: readonly TaskStateStyle[] = [
  {
    state: "planned",
    label: "Not started",
    glyph: "○",
    fg: "#9CA3AF",
    chipBg: "#F3F4F6",
  },
  {
    state: "in_progress",
    label: "In progress",
    glyph: "◐",
    fg: "#4F46E5",
    chipBg: "#EEF2FF",
  },
  {
    state: "blocked",
    label: "Blocked",
    glyph: "⊘",
    // Calm amber (§A9 / B8.2) — deliberately NOT a danger red.
    fg: "#B45309",
    chipBg: "#FEF3C7",
  },
  {
    state: "done",
    label: "Done",
    glyph: "●",
    fg: "#059669",
    chipBg: "#ECFDF5",
  },
] as const;

const BY_STATE: Record<TaskState, TaskStateStyle> = Object.fromEntries(
  TASK_STATES.map((s) => [s.state, s]),
) as Record<TaskState, TaskStateStyle>;

/** Resolve a state's full style. Falls back to "planned" for safety. */
export function taskStateStyle(state: TaskState): TaskStateStyle {
  return BY_STATE[state] ?? BY_STATE.planned;
}
